import { useLayoutEffect, useRef } from 'react';

/**
 * Денежный инпут для рублей с копейками.
 *
 * Хранит КАНОНИЧЕСКУЮ строку: точка-десятичный, без разделителей, ровно как ввёл
 * юзер — «500000.50» (хвостовой ноль НЕ теряется, в отличие от antd InputNumber).
 * Показывает по-русски: пробелы-разряды + запятая-десятичный → «500 000,50».
 *
 * Ввод: принимает и «,» и «.» как десятичный разделитель, режет всё кроме цифр,
 * ограничивает дробь 2 знаками (3-й ввести нельзя), чистит вставку. Курсор
 * восстанавливается по числу цифр слева (разряды-пробелы не сбивают каретку).
 */
export function toCanonicalMoney(raw: string): string {
    let s = (raw ?? '').replace(/,/g, '.').replace(/[^\d.]/g, '');
    const dot = s.indexOf('.');
    if (dot === -1) return s;
    const intPart = s.slice(0, dot);
    const decPart = s.slice(dot + 1).replace(/\./g, '').slice(0, 2);
    return intPart + '.' + decPart;
}

function formatMoney(canonical: string): string {
    if (canonical === '' || canonical == null) return '';
    const hasDot = canonical.includes('.');
    const [intPart, decPart] = canonical.split('.');
    const grouped = (intPart || '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return hasDot ? `${grouped},${decPart ?? ''}` : grouped;
}

function digitsBefore(s: string, upto: number): number {
    let n = 0;
    for (let i = 0; i < upto && i < s.length; i++) if (s[i] >= '0' && s[i] <= '9') n++;
    return n;
}

export function MoneyInput({ value, onChange, placeholder, disabled, status, style, bare }: {
    value: string;                       // каноническое «500000.50»
    onChange: (canonical: string) => void;
    placeholder?: string;
    disabled?: boolean;
    status?: 'error';
    style?: React.CSSProperties;
    /** bare=true — без инлайн-стилей: вид берётся из CSS виджета (.field input).
     *  Для welcome/offers (antd-форма без своих input-стилей) — false (дефолт). */
    bare?: boolean;
}) {
    const ref = useRef<HTMLInputElement>(null);
    const caretRef = useRef<number | null>(null);
    const display = formatMoney(value);

    useLayoutEffect(() => {
        if (caretRef.current != null && ref.current) {
            ref.current.setSelectionRange(caretRef.current, caretRef.current);
            caretRef.current = null;
        }
    });

    const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const caret = e.target.selectionStart ?? raw.length;
        const nDigits = digitsBefore(raw, caret);
        const canonical = toCanonicalMoney(raw);
        const formatted = formatMoney(canonical);
        // позиция после nDigits-й цифры в новом форматированном значении
        let pos = 0, seen = 0;
        while (pos < formatted.length && seen < nDigits) {
            if (formatted[pos] >= '0' && formatted[pos] <= '9') seen++;
            pos++;
        }
        caretRef.current = pos;
        onChange(canonical);
    };

    return (
        <input
            ref={ref}
            type="text"
            inputMode="decimal"
            value={display}
            onChange={onInput}
            placeholder={placeholder}
            disabled={disabled}
            style={bare
                ? (status === 'error' ? { borderColor: '#ff4d4f', ...style } : style)
                : {
                    width: '100%', height: 32, padding: '4px 11px', fontSize: 14,
                    borderRadius: 6, border: `1px solid ${status === 'error' ? '#ff4d4f' : '#d9d9d9'}`,
                    outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', ...style,
                }}
        />
    );
}
